import { Button } from '@sunwebgroup/uds-ui/button'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { useUdsToken } from '@sunwebgroup/uds-ui/utils'
import { AdvancedMarker, APIProvider, Map as GoogleMap } from '@vis.gl/react-google-maps'
import cn from 'classnames'
import Image from 'next/image'
import * as React from 'react'
import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import { MapAccoCard } from '@/scopes/common/components/MapAccoCard/MapAccoCard'
import { ActiveMarker } from '@/scopes/common/components/Markers/ActiveMarker'
import { InactiveMarker } from '@/scopes/common/components/Markers/InactiveMarker'
import { GOOGLE_MAP_ID } from '@/scopes/common/constants/GoogleMapId'
import { PublicationCodes } from '@/scopes/common/domain/models'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { destinationMapAPIClient } from '@/scopes/common/infrastructure/api/destination-map'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import { AccoCoordinates } from '@/scopes/products/domain/types'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import summerMapPlaceholder from './assets/summer-map-placeholder.svg'
import winterMapPlaceholder from './assets/winter-map-placeholder.svg'
import styles from './DestinationMap.module.css'

interface DestinationMapProperties extends React.HTMLAttributes<HTMLDivElement> {
  isHeroSection?: boolean
  lat: number
  lng: number
}

function DestinationMap({ className, isHeroSection = false, lat, lng }: DestinationMapProperties) {
  const intl = useIntl()
  const accommodationId = useCMSPageField('PDP.id')
  const [nearbyAccos, setNearbyAccos] = React.useState<AccoCoordinates[]>([])
  const [activeAccoId, setActiveAccoId] = React.useState<string | null>(accommodationId)
  const { sendEvent } = useAnalytics()
  const [isSheetOpen, setIsSheetOpen] = React.useState(false)
  const markerColor = useUdsToken<string>('primitives_color_primary_500')
  const publicationCode = useConfiguration('publicationCode')
  const googleMapsApiKey = useConfiguration('thirdParties.googleMapsApiKey')
  const isWinterSeason = PublicationCodes.isSunwebWinterPublicationCode(publicationCode)

  React.useEffect(() => {
    async function fetchNearbyAccos() {
      const { accommodations } = await destinationMapAPIClient.getNearbyAccos({ currentAccoId: accommodationId })
      setNearbyAccos(accommodations)
    }
    if (isSheetOpen) {
      fetchNearbyAccos()
    }
  }, [isSheetOpen])

  const handleBookNowClick = () => {
    sendEvent(
      createPDPClickEvent({
        element: 'Button',
        description: 'Clicked on book now button in map card',
        result: 'Map card book now button is clicked',
      }),
    )
  }

  const handleOpenChange = () => {
    const nextOpen = !isSheetOpen

    sendEvent(
      createPDPClickEvent({
        element: 'Bottom sheet',
        description: `${isHeroSection ? 'Top - ' : ''}Destination map bottom sheet ${nextOpen ? 'opened' : 'closed'}`,
        result: `Destination map bottom sheet ${nextOpen ? 'opened' : 'closed'}`,
      }),
    )

    setIsSheetOpen(nextOpen)
  }

  return (
    <div className={cn(styles.container, className)}>
      <Image
        className={styles.mapImage}
        src={isWinterSeason ? winterMapPlaceholder : summerMapPlaceholder}
        alt=""
        fill
      />
      <button
        type="button"
        className={styles.overlayButton}
        onClick={handleOpenChange}
        aria-label={intl.formatMessage({ id: 'destination_map_open_button' })}
      />
      <Sheet.Root open={isSheetOpen} onOpenChange={handleOpenChange}>
        <Sheet.Trigger>
          <Button className={styles.openButton} size="S" variant="overlayNeutral">
            <Button.Icon glyph="MapPinOnMap" />
            {intl.formatMessage({ id: 'destination_map_open_button' })}
          </Button>
        </Sheet.Trigger>
        <Sheet.Body side="bottom" hasPadding={false}>
          <Sheet.Header
            closeLabel={intl.formatMessage({ id: 'close' })}
            titleLabel={intl.formatMessage({ id: 'destination_map_title' })}
          />
          <Sheet.Content className={styles.sheetContent}>
            <APIProvider apiKey={googleMapsApiKey ?? ''}>
              <GoogleMap
                mapId={GOOGLE_MAP_ID}
                defaultZoom={12}
                defaultCenter={{ lat, lng }}
                reuseMaps
                fullscreenControl={false}
                headingInteractionEnabled={false}
                clickableIcons={false}
              >
                <NewRelicErrorBoundary scope="destination-map" fallback={null}>
                  <AdvancedMarker
                    zIndex={1000}
                    position={{ lat, lng }}
                    onClick={() => {
                      setActiveAccoId(accommodationId)
                    }}
                  >
                    <ActiveMarker color={markerColor} />
                  </AdvancedMarker>
                  {nearbyAccos?.map(acco => (
                    <AdvancedMarker
                      key={acco.id}
                      zIndex={1}
                      position={{ lat: acco.lat, lng: acco.lng }}
                      onClick={() => setActiveAccoId(acco.id)}
                    >
                      <InactiveMarker color={markerColor} />
                    </AdvancedMarker>
                  ))}
                </NewRelicErrorBoundary>
              </GoogleMap>
            </APIProvider>
            {activeAccoId && (
              <div className={styles.mapDetails}>
                <MapAccoCard accoId={activeAccoId} onBookNowClick={handleBookNowClick} />
              </div>
            )}
          </Sheet.Content>
        </Sheet.Body>
      </Sheet.Root>
    </div>
  )
}

export default DestinationMap
